// client/src/coms/views/canvas_parts/canvas_room_view_parts/canvasAreaAssignments.js

export const CANVAS_AREA_LAYOUTS = [
  { id: 'horizontal', label: 'Horizontal halves', slotLabels: ['Top', 'Bottom'] },
  { id: 'vertical', label: 'Vertical halves', slotLabels: ['Left', 'Right'] },
  { id: 'diagonal_down', label: 'Diagonal halves ↘', slotLabels: ['Upper / left', 'Lower / right'] },
  { id: 'diagonal_up', label: 'Diagonal halves ↗', slotLabels: ['Lower / left', 'Upper / right'] },
  { id: 'quarters', label: 'Four areas', slotLabels: ['Top left', 'Top right', 'Bottom left', 'Bottom right'] },
];

const DEFAULT_ASSIGNMENTS = {
  enabled: false,
  layout: 'horizontal',
  splitX: 50,
  splitY: 50,
  slots: {},
};

function clampPercent(value, fallback = 50) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.max(10, Math.min(90, Math.round(number)));
}

function sanitizeCanvasAreaClientId(value) {
  return String(value || '').trim();
}

function makeCanvasAreaOwnerKey(ownerKey, ownerUserId, ownerClientId) {
  const explicitOwnerKey = String(ownerKey || '').trim();
  if (explicitOwnerKey) return explicitOwnerKey;

  const safeOwnerUserId = Number(ownerUserId);
  if (Number.isInteger(safeOwnerUserId) && safeOwnerUserId > 0) {
    return 'user-' + safeOwnerUserId;
  }

  const safeOwnerClientId = sanitizeCanvasAreaClientId(ownerClientId);
  if (safeOwnerClientId) {
    return 'client-' + safeOwnerClientId.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 48);
  }

  return '';
}

export function sanitizeCanvasAreaAssignments(value) {
  const source = value && typeof value === 'object' ? value : {};
  const layout = CANVAS_AREA_LAYOUTS.some((item) => item.id === source.layout) ? source.layout : DEFAULT_ASSIGNMENTS.layout;
  const sourceSlots = source.slots && typeof source.slots === 'object' ? source.slots : {};
  const slots = {};

  Object.keys(sourceSlots).forEach((slotId) => {
    const slot = sourceSlots[slotId];
    if (!slot || typeof slot !== 'object') return;
    const ownerName = String(slot.ownerName || '').trim();
    const ownerUserId = Number(slot.ownerUserId);
    const ownerClientId = sanitizeCanvasAreaClientId(slot.ownerClientId);
    const safeOwnerUserId = Number.isInteger(ownerUserId) && ownerUserId > 0 ? ownerUserId : 0;
    const ownerKey = makeCanvasAreaOwnerKey(slot.ownerKey, safeOwnerUserId, ownerClientId);
    if (!ownerKey) return;
    slots[slotId] = {
      ownerKey,
      ownerName,
      ownerUserId: safeOwnerUserId,
      ownerClientId,
    };
  });

  return {
    enabled: !!source.enabled,
    layout,
    splitX: clampPercent(source.splitX, DEFAULT_ASSIGNMENTS.splitX),
    splitY: clampPercent(source.splitY, DEFAULT_ASSIGNMENTS.splitY),
    slots,
  };
}

export function getCanvasAreaLayoutDefinition(layout) {
  return CANVAS_AREA_LAYOUTS.find((item) => item.id === layout) || CANVAS_AREA_LAYOUTS[0];
}

export function getCanvasAreaSlotIds(areaAssignments) {
  const safeAssignments = sanitizeCanvasAreaAssignments(areaAssignments);
  const layout = getCanvasAreaLayoutDefinition(safeAssignments.layout);
  return layout.slotLabels.map((label, index) => ({ id: String(index), label }));
}

export function getLayerOwnerKeyForArea(layer) {
  if (!layer) return '';
  return makeCanvasAreaOwnerKey(layer.ownerKey, layer.ownerUserId, layer.ownerClientId);
}

export function getCurrentUserAreaOwnerKey(currentUserId, liveClientIdRef) {
  const userId = Number(currentUserId);
  if (Number.isInteger(userId) && userId > 0) return 'user-' + userId;
  const clientId = sanitizeCanvasAreaClientId(liveClientIdRef && liveClientIdRef.current ? liveClientIdRef.current : '');
  return makeCanvasAreaOwnerKey('', 0, clientId);
}

function normaliseAreaUserId(value) {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

function getCanvasUserName(value, fallbackId = 0) {
  if (!value || typeof value !== 'object') return fallbackId > 0 ? 'User ' + fallbackId : '';
  return String(
    value.display_name
    || value.displayName
    || value.username
    || value.name
    || value.email
    || value.full_name
    || value.fullName
    || ''
  ).trim() || (fallbackId > 0 ? 'User ' + fallbackId : '');
}

function getCanvasUserId(value) {
  if (!value || typeof value !== 'object') return 0;
  return normaliseAreaUserId(value.id || value.user_id || value.userId || value.owner_user_id || value.other_user_id);
}

function collectCanvasAreaDirectoryObjects(directory) {
  const items = Array.isArray(directory) ? directory : [];
  const users = [];

  items.forEach((item) => {
    if (!item || typeof item !== 'object') return;
    if (item.user && typeof item.user === 'object') users.push(item.user);
    if (item.other_user && typeof item.other_user === 'object') users.push(item.other_user);
    if (item.otherUser && typeof item.otherUser === 'object') users.push(item.otherUser);
    if (item.friend && typeof item.friend === 'object') users.push(item.friend);
    if (item.friend_user && typeof item.friend_user === 'object') users.push(item.friend_user);
    if (item.friendUser && typeof item.friendUser === 'object') users.push(item.friendUser);
    users.push(item);
  });

  return users.filter((item) => item && typeof item === 'object');
}

function collectCanvasUserObjects(canvas) {
  if (!canvas || typeof canvas !== 'object') return [];
  const buckets = [
    canvas.owner_user,
    canvas.ownerUser,
    canvas.owner,
    canvas.created_by_user,
    canvas.createdByUser,
    canvas.user,
    canvas.creator,
  ];

  [
    canvas.participant_users,
    canvas.participantUsers,
    canvas.participants,
    canvas.users,
    canvas.members,
    canvas.canvas_users,
    canvas.canvasUsers,
  ].forEach((list) => {
    if (Array.isArray(list)) {
      list.forEach((item) => buckets.push(item && item.user && typeof item.user === 'object' ? item.user : item));
    }
  });

  return buckets.filter((item) => item && typeof item === 'object');
}

function addCanvasAreaUser(byKey, user) {
  if (!user || typeof user !== 'object') return;
  const ownerUserId = normaliseAreaUserId(user.ownerUserId || user.userId || user.id);
  const ownerClientId = String(user.ownerClientId || '').trim();
  const ownerKey = String(user.ownerKey || (ownerUserId > 0 ? 'user-' + ownerUserId : '') || (ownerClientId ? 'client-' + ownerClientId.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 48) : '')).trim();
  if (!ownerKey) return;

  const ownerName = String(user.ownerName || '').trim() || (ownerUserId > 0 ? 'User ' + ownerUserId : ownerKey);
  const existing = byKey.get(ownerKey);

  if (existing) {
    const existingName = String(existing.ownerName || '').trim();
    const existingLooksGeneric = !existingName || existingName === ownerKey || /^User\s+\d+$/i.test(existingName);
    const nextLooksBetter = ownerName && ownerName !== ownerKey && !/^User\s+\d+$/i.test(ownerName);
    if (existingLooksGeneric || nextLooksBetter) {
      byKey.set(ownerKey, {
        ...existing,
        ownerUserId: existing.ownerUserId || ownerUserId,
        ownerClientId: existing.ownerClientId || ownerClientId,
        ownerName,
      });
    }
    return;
  }

  byKey.set(ownerKey, {
    ownerKey,
    ownerUserId,
    ownerClientId,
    ownerName,
  });
}

export function getCanvasAreaUsersFromLayers(layers, currentUserId, currentUserName, liveClientIdRef, canvas, userDirectory = []) {
  const byKey = new Map();
  const localKey = getCurrentUserAreaOwnerKey(currentUserId, liveClientIdRef);
  const localUserId = normaliseAreaUserId(currentUserId);
  const localName = String(currentUserName || '').trim() || 'Me';

  addCanvasAreaUser(byKey, {
    ownerKey: localKey,
    userId: localUserId,
    ownerClientId: String(liveClientIdRef && liveClientIdRef.current ? liveClientIdRef.current : '').trim(),
    ownerName: localName,
  });

  const ownerUserId = normaliseAreaUserId(canvas && canvas.owner_user_id);
  if (ownerUserId > 0) {
    const ownerObject = collectCanvasUserObjects(canvas).find((item) => getCanvasUserId(item) === ownerUserId) || null;
    addCanvasAreaUser(byKey, {
      userId: ownerUserId,
      ownerName: ownerUserId === localUserId ? localName : getCanvasUserName(ownerObject, ownerUserId),
    });
  }

  const canvasUsersById = new Map();
  collectCanvasUserObjects(canvas).forEach((item) => {
    const userId = getCanvasUserId(item);
    if (userId > 0) canvasUsersById.set(userId, item);
  });

  const directoryUsersById = new Map();
  collectCanvasAreaDirectoryObjects(userDirectory).forEach((item) => {
    const userId = getCanvasUserId(item);
    if (userId > 0 && !directoryUsersById.has(userId)) directoryUsersById.set(userId, item);
  });

  const allowedUserIds = new Set();
  if (localUserId > 0) allowedUserIds.add(localUserId);
  if (ownerUserId > 0) allowedUserIds.add(ownerUserId);

  const participantIds = Array.isArray(canvas && canvas.participant_user_ids) ? canvas.participant_user_ids : [];
  participantIds.forEach((value) => {
    const userId = normaliseAreaUserId(value);
    if (userId > 0) allowedUserIds.add(userId);
  });

  collectCanvasUserObjects(canvas).forEach((item) => {
    const userId = getCanvasUserId(item);
    if (userId > 0) allowedUserIds.add(userId);
  });

  allowedUserIds.forEach((userId) => {
    if (userId <= 0) return;
    const userObject = canvasUsersById.get(userId) || directoryUsersById.get(userId) || null;
    addCanvasAreaUser(byKey, {
      userId,
      ownerName: userId === localUserId ? localName : getCanvasUserName(userObject, userId),
    });
  });

  (Array.isArray(layers) ? layers : []).forEach((layer) => {
    const ownerKey = getLayerOwnerKeyForArea(layer);
    if (!ownerKey) return;
    addCanvasAreaUser(byKey, {
      ownerKey,
      ownerUserId: normaliseAreaUserId(layer.ownerUserId),
      ownerClientId: String(layer.ownerClientId || '').trim(),
      ownerName: String(layer.ownerName || layer.name || ownerKey).trim(),
    });
  });

  return Array.from(byKey.values()).slice(0, 16);
}


function makeCanvasAreaSelection(slotId, safeAssignments, stageSize, layerId = null) {
  const width = Math.max(1, Number(stageSize && stageSize.width) || 1);
  const height = Math.max(1, Number(stageSize && stageSize.height) || 1);
  const splitX = width * (safeAssignments.splitX / 100);
  const splitY = height * (safeAssignments.splitY / 100);
  const id = String(slotId);
  let vertices = [];

  if (safeAssignments.layout === 'vertical') {
    vertices = id === '0'
      ? [{ x: 0, y: 0 }, { x: splitX, y: 0 }, { x: splitX, y: height }, { x: 0, y: height }]
      : [{ x: splitX, y: 0 }, { x: width, y: 0 }, { x: width, y: height }, { x: splitX, y: height }];
  } else if (safeAssignments.layout === 'diagonal_down') {
    vertices = id === '0'
      ? [{ x: 0, y: 0 }, { x: width, y: 0 }, { x: width, y: height }]
      : [{ x: 0, y: 0 }, { x: width, y: height }, { x: 0, y: height }];
  } else if (safeAssignments.layout === 'diagonal_up') {
    vertices = id === '0'
      ? [{ x: 0, y: height }, { x: width, y: height }, { x: width, y: 0 }]
      : [{ x: 0, y: 0 }, { x: width, y: 0 }, { x: 0, y: height }];
  } else if (safeAssignments.layout === 'quarters') {
    if (id === '0') vertices = [{ x: 0, y: 0 }, { x: splitX, y: 0 }, { x: splitX, y: splitY }, { x: 0, y: splitY }];
    if (id === '1') vertices = [{ x: splitX, y: 0 }, { x: width, y: 0 }, { x: width, y: splitY }, { x: splitX, y: splitY }];
    if (id === '2') vertices = [{ x: 0, y: splitY }, { x: splitX, y: splitY }, { x: splitX, y: height }, { x: 0, y: height }];
    if (id === '3') vertices = [{ x: splitX, y: splitY }, { x: width, y: splitY }, { x: width, y: height }, { x: splitX, y: height }];
  } else {
    vertices = id === '0'
      ? [{ x: 0, y: 0 }, { x: width, y: 0 }, { x: width, y: splitY }, { x: 0, y: splitY }]
      : [{ x: 0, y: splitY }, { x: width, y: splitY }, { x: width, y: height }, { x: 0, y: height }];
  }

  if (!vertices.length) return null;
  return {
    layerId,
    shape: 'polygon',
    bounds: {
      x: 0,
      y: 0,
      width,
      height,
    },
    vertices,
    floatingImageData: null,
    inverted: false,
  };
}

export function getCanvasAreaSelectionForOwner(areaAssignments, stageSize, ownerKey, layerId = null) {
  const safeAssignments = sanitizeCanvasAreaAssignments(areaAssignments);
  if (!safeAssignments.enabled) return null;
  const safeOwnerKey = String(ownerKey || '').trim();
  if (!safeOwnerKey) return null;

  const matchingSelections = Object.keys(safeAssignments.slots || {}).map((slotId) => {
    const slot = safeAssignments.slots[slotId];
    if (!slot || String(slot.ownerKey || '').trim() !== safeOwnerKey) return null;
    return makeCanvasAreaSelection(slotId, safeAssignments, stageSize, layerId);
  }).filter(Boolean);

  if (!matchingSelections.length) return null;
  if (matchingSelections.length === 1) return matchingSelections[0];

  const width = Math.max(1, Number(stageSize && stageSize.width) || 1);
  const height = Math.max(1, Number(stageSize && stageSize.height) || 1);
  return {
    layerId,
    shape: 'canvas_area_slots',
    bounds: { x: 0, y: 0, width, height },
    slots: matchingSelections,
    floatingImageData: null,
    inverted: false,
  };
}

export function combineCanvasAreaSelectionWithSelection(areaSelection, baseSelection) {
  if (!areaSelection || !areaSelection.bounds) return baseSelection || null;
  if (!baseSelection || !baseSelection.bounds) return areaSelection;
  return {
    layerId: baseSelection.layerId || areaSelection.layerId || null,
    shape: 'combined_clip',
    bounds: areaSelection.bounds,
    canvasAreaSelection: areaSelection,
    baseSelection,
    floatingImageData: null,
    inverted: false,
  };
}

export function getCanvasAreaSlotForPoint(point, areaAssignments, stageSize) {
  if (!point || !stageSize) return '';
  const safeAssignments = sanitizeCanvasAreaAssignments(areaAssignments);
  const width = Math.max(1, Number(stageSize.width) || 1);
  const height = Math.max(1, Number(stageSize.height) || 1);
  const x = Number(point.x);
  const y = Number(point.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return '';

  const splitX = width * (safeAssignments.splitX / 100);
  const splitY = height * (safeAssignments.splitY / 100);

  if (safeAssignments.layout === 'vertical') return x <= splitX ? '0' : '1';
  if (safeAssignments.layout === 'diagonal_down') return (y / height) <= (x / width) ? '0' : '1';
  if (safeAssignments.layout === 'diagonal_up') return (y / height) <= (1 - (x / width)) ? '1' : '0';
  if (safeAssignments.layout === 'quarters') {
    if (x <= splitX && y <= splitY) return '0';
    if (x > splitX && y <= splitY) return '1';
    if (x <= splitX && y > splitY) return '2';
    return '3';
  }
  return y <= splitY ? '0' : '1';
}

export function canAreaOwnerDrawAtPoint(point, areaAssignments, stageSize, ownerKey) {
  const safeAssignments = sanitizeCanvasAreaAssignments(areaAssignments);
  if (!safeAssignments.enabled) return true;
  const safeOwnerKey = String(ownerKey || '').trim();
  if (!safeOwnerKey) return false;
  const slotId = getCanvasAreaSlotForPoint(point, safeAssignments, stageSize);
  if (!slotId) return false;
  const slot = safeAssignments.slots[slotId];
  if (!slot || !slot.ownerKey) return false;
  return String(slot.ownerKey).trim() === safeOwnerKey;
}
